/**
 * Generation History Section Component
 * Displays batch history with per-batch CSV download functionality
 * Used within CodeRetrievalModal as a tab
 */

import { useEffect, useMemo, useState } from 'react';
import { useBatchHistory } from '../../hooks/useRedeemGroupApi';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { formatDate } from '../../lib/utils/formatters';
import toast from 'react-hot-toast';
import type { Batch } from '../../lib/types/redeemGroup';

interface GenerationHistorySectionProps {
  groupId: string;
  groupName: string;
}

export function GenerationHistorySection({
  groupId,
  groupName,
}: GenerationHistorySectionProps) {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useBatchHistory(groupId);

  // Track which batch is currently being downloaded
  const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(
    null
  );

  // Track which batch's dropdown menu is open
  const [openMenuBatchId, setOpenMenuBatchId] = useState<string | null>(null);

  // Flatten all pages into single array
  const allBatches = useMemo(() => {
    return data?.pages.flatMap(page => page.batches) || [];
  }, [data?.pages]);

  // Download CSV for a specific batch using streaming endpoint
  const handleDownloadBatchCSV = async (
    batch: Batch,
    unredeemedOnly: boolean
  ) => {
    setOpenMenuBatchId(null);
    setDownloadingBatchId(batch.id);
    try {
      // Call streaming CSV endpoint
      const { authenticatedApi } = await import('../../lib/authenticated-api');
      const blob = await authenticatedApi.downloadGroupCodesCSV(groupId, {
        batchId: batch.id,
        redeemed: unredeemedOnly ? false : undefined,
      });

      // Generate filename with batch-specific timestamp
      const timestamp = new Date(batch.createdAt)
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, -5);
      const sanitizedGroupName = groupName.replace(/[^a-zA-Z0-9-_]/g, '_');
      const suffix = unredeemedOnly ? '-unredeemed' : '';
      const filename = `redeem-codes-${sanitizedGroupName}-batch${suffix}-${timestamp}.csv`;

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delay revocation to ensure download completes before releasing memory
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
    } catch (err) {
      console.error('Failed to download batch CSV:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to download CSV';
      toast.error(errorMessage);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!openMenuBatchId) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest('[aria-haspopup="true"]') &&
        !target.closest('[role="menu"]')
      ) {
        setOpenMenuBatchId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuBatchId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-2">
            Failed to load generation history
          </p>
          <button
            onClick={() => refetch()}
            className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (allBatches.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">
          No generation history found
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Batch Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-96">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Note
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Generated By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {allBatches.map(batch => (
                <tr
                  key={batch.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(batch.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {batch.generatedCount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        batch.source === 'generated'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      }`}
                    >
                      {batch.source === 'generated' ? (
                        <>
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                          </svg>
                          Generated
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Imported
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {batch.note ? (
                      <span
                        className="inline-block max-w-xs truncate"
                        title={batch.note}
                      >
                        {batch.note}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-600">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {batch.createdByDisplayName || batch.createdBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        batch.status === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : batch.status === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}
                    >
                      {batch.status.charAt(0).toUpperCase() +
                        batch.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="relative inline-block">
                      <button
                        onClick={() =>
                          setOpenMenuBatchId(
                            openMenuBatchId === batch.id ? null : batch.id
                          )
                        }
                        disabled={
                          batch.status !== 'completed' ||
                          downloadingBatchId === batch.id
                        }
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={`Download CSV for batch from ${formatDate(batch.createdAt)}`}
                        aria-haspopup="true"
                        aria-expanded={openMenuBatchId === batch.id}
                      >
                        {downloadingBatchId === batch.id ? (
                          <>
                            <LoadingSpinner size="sm" className="mr-1.5" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4 mr-1.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                            CSV
                            <svg
                              className="w-3 h-3 ml-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </>
                        )}
                      </button>
                      {openMenuBatchId === batch.id && (
                        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-50">
                          <div
                            className="py-1"
                            role="menu"
                            aria-orientation="vertical"
                          >
                            <button
                              onClick={() =>
                                handleDownloadBatchCSV(batch, false)
                              }
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                              role="menuitem"
                            >
                              Download all codes
                            </button>
                            <button
                              onClick={() =>
                                handleDownloadBatchCSV(batch, true)
                              }
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                              role="menuitem"
                            >
                              Download unredeemed codes only
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More Button */}
      {hasNextPage && (
        <div className="mt-4">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
